import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import api, { getBackendOrigin } from "../api/client";
import { Upload, Download, Printer, Plus, Trash2, Edit3, FileSpreadsheet, CheckCircle2, AlertCircle, FileText, Layers, Search, Filter } from "lucide-react";

export function VoucherPrinterScreen() {
  const [vouchers, setVouchers] = useState([
    {
      id: "v-1",
      expenseDate: new Date().toLocaleDateString('en-GB').replace(/\//g, '-'),
      particulars: "MR Enterprises",
      header: "Travel",
      description: "Travel to Mount Road client site for installation",
      amount: 1250,
      paymentMode: "Online",
      status: "APPROVED",
      approvedBy: "Vijay Rajagopalan"
    },
    {
      id: "v-2",
      expenseDate: new Date().toLocaleDateString('en-GB').replace(/\//g, '-'),
      particulars: "Senco Gold Ltd",
      header: "Food & Refreshments",
      description: "Lunch & Refreshments during gold audit in Poonamallee",
      amount: 450,
      paymentMode: "Online",
      status: "APPROVED",
      approvedBy: "Vijay Rajagopalan"
    }
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHeaderFilter, setSelectedHeaderFilter] = useState("ALL");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [availableHeaders, setAvailableHeaders] = useState([]);
  const [dbSaving, setDbSaving] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Search DB Vouchers by header, text, or month
  const fetchDbVouchers = async (q = searchQuery, hdr = selectedHeaderFilter, mo = selectedMonth) => {
    try {
      const res = await api.get("/reimbursements/vouchers/search", {
        params: { query: q, header: hdr, month: mo }
      });
      if (res.data && res.data.vouchers && res.data.vouchers.length > 0) {
        setVouchers(res.data.vouchers);
      }
      if (res.data && res.data.headers) {
        setAvailableHeaders(res.data.headers);
      }
    } catch (err) {
      console.error("Fetch DB Vouchers Error:", err);
    }
  };

  useEffect(() => {
    fetchDbVouchers();
  }, []);

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    fetchDbVouchers(val, selectedHeaderFilter, selectedMonth);
  };

  const handleHeaderFilterChange = (val) => {
    setSelectedHeaderFilter(val);
    fetchDbVouchers(searchQuery, val, selectedMonth);
  };

  const handleMonthFilterChange = (val) => {
    setSelectedMonth(val);
    fetchDbVouchers(searchQuery, selectedHeaderFilter, val);
  };

  // Download Sample Excel Template
  const handleDownloadSampleTemplate = () => {
    const sampleData = [
      {
        "Date": "25-09-2026",
        "Particulars": "M.R. Enterprises",
        "Header": "Travel Expense",
        "Description": "Taxi fare for client meeting at Mount Road",
        "Amount": 650,
        "Mode of Payment": "Online",
        "Status": "APPROVED",
        "Approved By": "Vijay Rajagopalan"
      },
      {
        "Date": "25-09-2026",
        "Particulars": "Hotel Saravana Bhavan",
        "Header": "Food & Refreshments",
        "Description": "Team lunch during gold audit",
        "Amount": 420,
        "Mode of Payment": "Online",
        "Status": "APPROVED",
        "Approved By": "Vijay Rajagopalan"
      },
      {
        "Date": "26-09-2026",
        "Particulars": "Reliance Digital",
        "Header": "Office Accessories",
        "Description": "HDMI adapter cable for machine presentation",
        "Amount": 1190,
        "Mode of Payment": "Online",
        "Status": "APPROVED",
        "Approved By": "Vijay Rajagopalan"
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");
    XLSX.writeFile(workbook, "Expense_Payment_Vouchers_Sample_Template.xlsx");
  };

  // Helper to extract flexible column values from raw Excel row object
  const getCol = (row, candidates) => {
    const keys = Object.keys(row);
    for (const cand of candidates) {
      const matchKey = keys.find(k => k.trim().toLowerCase() === cand.toLowerCase() || k.trim().toLowerCase().includes(cand.toLowerCase()));
      if (matchKey && row[matchKey] !== undefined && row[matchKey] !== null) {
        return String(row[matchKey]).trim();
      }
    }
    return "";
  };

  // Handle Excel file upload & Save to DB
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMsg("");
    setSuccessMsg("");
    setDbSaving(true);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary", cellDates: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { raw: false, dateNF: "yyyy-mm-dd" });

        if (!data || data.length === 0) {
          setErrorMsg("No data found in uploaded Excel file.");
          setDbSaving(false);
          return;
        }

        const parsedList = data.map((r, i) => {
          const rawAmt = getCol(r, ["amount", "debit", "rs", "val", "total", "debit (rs)"]);
          const numAmt = parseFloat(rawAmt.replace(/[^0-9.]/g, "")) || 0;

          return {
            id: `excel-${Date.now()}-${i}`,
            expenseDate: getCol(r, ["date", "expense date", "claim date"]) || new Date().toLocaleDateString('en-GB').replace(/\//g, '-'),
            particulars: getCol(r, ["particulars", "particular", "pay to (supplier)", "supplier", "pay to", "vendor"]) || "",
            header: getCol(r, ["header", "expense header", "category", "type", "head"]) || "General Expense",
            description: getCol(r, ["description", "purpose", "remarks", "details"]) || "Expense Claim",
            amount: numAmt,
            paymentMode: getCol(r, ["mode of payment", "payment mode", "mode", "pay mode"]) || "Online",
            status: getCol(r, ["status"]) || "APPROVED",
            approvedBy: getCol(r, ["approved by", "authoriser", "signatory"]) || "Vijay Rajagopalan"
          };
        });

        setVouchers(parsedList);

        // Save parsed vouchers to Database!
        try {
          const saveRes = await api.post("/reimbursements/vouchers/upload-excel", { vouchers: parsedList });
          if (saveRes.data && saveRes.data.vouchers) {
            setVouchers(saveRes.data.vouchers);
          }
          setSuccessMsg(`Successfully imported & stored ${parsedList.length} vouchers in database!`);
          fetchDbVouchers();
        } catch (dbErr) {
          console.error("Failed to store vouchers in DB:", dbErr);
          const errorMsgStr = dbErr.response?.data?.error || "Failed to store vouchers in Database.";
          alert(errorMsgStr);
          setErrorMsg(errorMsgStr);
          fetchDbVouchers();
        }
      } catch (err) {
        console.error("Excel parse error:", err);
        setErrorMsg("Failed to read Excel file. Please ensure it is a valid .xlsx or .xls file.");
      } finally {
        setDbSaving(false);
      }
    };
    reader.readAsBinaryString(file);
  };



  // Delete Row
  const handleDeleteRow = async (id, dbId) => {
    const pwd = prompt("Enter password to delete this voucher:");
    if (!pwd) return;
    if (pwd !== "12345") {
      setErrorMsg("Incorrect password. Deletion cancelled.");
      return;
    }
    setErrorMsg("");

    if (dbId) {
      try {
        await api.delete(`/reimbursements/vouchers/printed/${dbId}`);
      } catch (err) {
        console.error("Delete error:", err);
        setErrorMsg("Failed to delete from database.");
        return;
      }
    }
    setVouchers(prev => prev.filter(v => v.id !== id));
    setSuccessMsg("Voucher deleted successfully.");
  };

  // Generate & Print PDF (2 Vouchers per A4 Page)
  const handleGeneratePdf = async () => {
    if (vouchers.length === 0) {
      setErrorMsg("Please add or upload at least one voucher to generate PDF.");
      return;
    }

    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const token = localStorage.getItem("fp_token");
      const res = await fetch(`${getBackendOrigin()}/api/reimbursements/vouchers/print-excel-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ vouchers })
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || "Server failed to generate PDF");
      }

      const blob = await res.blob();
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, "_blank");

      setSuccessMsg(`Generated A4 PDF containing ${vouchers.length} voucher(s) (${Math.ceil(vouchers.length / 2)} page(s), 2 vouchers per page)!`);
    } catch (err) {
      console.error("PDF generation error:", err);
      setErrorMsg(err.message || "Failed to generate PDF.");
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = vouchers.reduce((sum, v) => sum + (parseFloat(v.amount) || 0), 0);
  const totalPages = Math.ceil(vouchers.length / 2);

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header Banner */}
      <Card style={{
        background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        color: "#fff",
        marginBottom: 20,
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: `${TOKENS.gold}22`, border: `1.5px solid ${TOKENS.gold}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Printer size={26} color={TOKENS.gold} />
          </div>
          <div>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "#fff", margin: 0 }}>
              Expense / Payment Voucher Printer
            </h2>
            <p style={{ fontSize: 13, color: TOKENS.goldLight, margin: "3px 0 0", fontWeight: 500 }}>
              Upload Excel sheet, store in DB, search by Header & print Expense / Payment Vouchers (Formatted 2 Vouchers per A4 Page)
            </p>
          </div>
        </div>
      </Card>

      {/* Action Bar */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <label style={{
          flex: "1 1 240px",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          background: TOKENS.navyDeep, color: "#fff", borderRadius: 12,
          padding: "12px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
          boxShadow: "0 4px 12px rgba(10,25,50,0.15)"
        }}>
          <Upload size={18} color={TOKENS.gold} />
          <span>Upload Excel Vouchers (.xlsx / .csv)</span>
          <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} style={{ display: "none" }} />
        </label>

        <button
          onClick={handleDownloadSampleTemplate}
          style={{
            flex: "1 1 200px",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: "#fff", color: TOKENS.navyDeep, border: `1.5px solid ${TOKENS.navyDeep}`,
            borderRadius: 12, padding: "12px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}
        >
          <FileSpreadsheet size={18} color={TOKENS.navyDeep} />
          <span>Download Sample Excel Template</span>
        </button>

        <button
          onClick={handleGeneratePdf}
          disabled={loading || vouchers.length === 0}
          style={{
            flex: "1 1 260px",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: `linear-gradient(135deg, ${TOKENS.gold}, #D4AF37)`, color: TOKENS.navyDeep,
            border: "none", borderRadius: 12, padding: "12px 18px", fontSize: 13.5, fontWeight: 800,
            cursor: loading || vouchers.length === 0 ? "not-allowed" : "pointer",
            boxShadow: `0 4px 14px ${TOKENS.gold}40`
          }}
        >
          {loading ? (
            <>
              <span style={{ animation: "spin 1s linear infinite" }}>⟳</span>
              <span>Generating PDF...</span>
            </>
          ) : (
            <>
              <Printer size={18} color={TOKENS.navyDeep} />
              <span>🖨️ Print PDF (2 Vouchers per A4 Page)</span>
            </>
          )}
        </button>
      </div>

      {/* Messages */}
      {successMsg && (
        <div style={{
          background: "#E6F4EA", border: `1px solid ${TOKENS.success}`,
          borderRadius: 10, padding: "12px 16px", marginBottom: 16,
          display: "flex", alignItems: "center", gap: 10, color: "#137333", fontSize: 13, fontWeight: 600,
        }}>
          <CheckCircle2 size={20} color={TOKENS.success} />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div style={{
          background: "#FCE8E6", border: `1px solid ${TOKENS.danger}`,
          borderRadius: 10, padding: "12px 16px", marginBottom: 16,
          display: "flex", alignItems: "center", gap: 10, color: "#C5221F", fontSize: 13, fontWeight: 600,
        }}>
          <AlertCircle size={20} color={TOKENS.danger} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.navyDeep }}>
            {vouchers.length}
          </div>
          <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5, fontWeight: 600 }}>TOTAL VOUCHERS</div>
        </Card>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.success }}>
            ₹{totalAmount.toLocaleString("en-IN")}
          </div>
          <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5, fontWeight: 600 }}>TOTAL EXPENSE AMOUNT</div>
        </Card>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.primary }}>
            {totalPages} {totalPages === 1 ? "Page" : "Pages"}
          </div>
          <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5, fontWeight: 600 }}>A4 PDF PAGE COUNT (2/PAGE)</div>
        </Card>
      </div>

      {/* Vouchers Table Card */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", background: TOKENS.cream, borderBottom: `1px solid ${TOKENS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <SectionLabel icon={FileText} label="STORED & EXCEL VOUCHERS LIST" />
          
          {/* Search & Header Filter Bar */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ position: "relative", minWidth: 200 }}>
              <Search size={14} color={TOKENS.muted} style={{ position: "absolute", left: 10, top: 10 }} />
              <input
                type="text"
                placeholder="Search header, particulars, desc..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{ width: "100%", padding: "6px 10px 6px 30px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 12, outline: "none" }}
              />
            </div>

            {availableHeaders.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Filter size={14} color={TOKENS.muted} />
                <select
                  value={selectedHeaderFilter}
                  onChange={(e) => handleHeaderFilterChange(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 12, background: "#fff", outline: "none", fontWeight: 600 }}
                >
                  <option value="ALL">All Headers</option>
                  {availableHeaders.map((h, i) => (
                    <option key={i} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input 
                type="month"
                value={selectedMonth}
                onChange={(e) => handleMonthFilterChange(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 12, background: "#fff", outline: "none", fontWeight: 600 }}
              />
            </div>
          </div>
        </div>

        {vouchers.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: TOKENS.muted }}>
            <FileSpreadsheet size={36} color={TOKENS.border} style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: TOKENS.navySoft }}>No vouchers to display</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Upload an Excel file to get started.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#F8FAFC", borderBottom: `1.5px solid ${TOKENS.border}` }}>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep, fontWeight: 700, width: 40 }}>#</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep, fontWeight: 700, minWidth: 110 }}>Date</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep, fontWeight: 700, minWidth: 130 }}>Header</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep, fontWeight: 700, minWidth: 150 }}>Particulars</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep, fontWeight: 700, minWidth: 200 }}>Description</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep, fontWeight: 700, minWidth: 100, textAlign: "right" }}>Amount</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep, fontWeight: 700, minWidth: 130 }}>Mode of Payment</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep, fontWeight: 700, minWidth: 70, textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v, idx) => (
                  <tr key={v.id} style={{ borderBottom: `1px solid ${TOKENS.border}`, background: idx % 2 === 0 ? "#fff" : "#F9FBFC" }}>
                    <td style={{ padding: "8px 12px", fontWeight: 700, color: TOKENS.muted }}>{idx + 1}</td>

                    <td style={{ padding: "6px 8px" }}>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 600 }}>{v.expenseDate}</span>
                    </td>

                    <td style={{ padding: "6px 8px" }}>
                      <span style={{ display: "block", fontSize: 12 }}>{v.header}</span>
                    </td>

                    <td style={{ padding: "6px 8px" }}>
                      <span style={{ display: "block", fontSize: 12 }}>{v.particulars}</span>
                    </td>

                    <td style={{ padding: "6px 8px" }}>
                      <span style={{ display: "block", fontSize: 12 }}>{v.description}</span>
                    </td>

                    <td style={{ padding: "6px 8px", textAlign: "right", color: TOKENS.navyDeep, fontWeight: 700 }}>
                      <span style={{ display: "block", fontSize: 12 }}>{v.amount}</span>
                    </td>

                    <td style={{ padding: "6px 8px" }}>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 600 }}>{v.paymentMode || "Online"}</span>
                    </td>

                    <td style={{ padding: "6px 8px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDeleteRow(v.id, v.dbId)}
                        title="Delete Row"
                        style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}
                      >
                        <Trash2 size={16} color={TOKENS.danger} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
